import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getWiziQuizAttemptResult,
  getWiziQuizLeaderboard,
  isWiziQuizSuccess,
  getWiziQuizErrorMessage,
} from '../utils/wiziQuizApiClient';

interface Question {
  id: number;
  question_order: number;
  marks: number;
  negative_marks: number;
  user_answer: string | null;
  correct_answer: string;
  is_correct: boolean;
  status: string;
  time_spent: number;
  marks_obtained: number;
  question_img_url: string;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  solution: string;
  subject_id: number;
  chapter_name: string;
  question_type: 'mcq' | 'integer';
}

interface AttemptResult {
  id: number;
  wizi_quiz_id: number;
  attempt_number: number;
  total_score: number;
  total_marks: number;
  accuracy_percentage: number;
  correct_answers: number;
  incorrect_answers: number;
  skipped_questions: number;
  total_questions: number;
  time_spent: number;
  rank: number | null;
}

const WiziQuizResults: React.FC = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();

  const [result, setResult] = useState<AttemptResult | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedQuestion, setExpandedQuestion] = useState<number | null>(null);

  useEffect(() => {
    loadResults();
  }, [attemptId]);

  const loadResults = async () => {
    if (!attemptId) {
      setError('Attempt ID not provided');
      setLoading(false);
      return;
    }

    try {
      const response = await getWiziQuizAttemptResult(parseInt(attemptId));

      if (!isWiziQuizSuccess(response)) {
        throw new Error(getWiziQuizErrorMessage(response));
      }

      const { attempt, questions: fetchedQuestions } = response.result;
      setResult(attempt);
      setQuestions(fetchedQuestions);
    } catch (err) {
      console.error('Error loading results:', err);
      setError(err instanceof Error ? err.message : 'Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${mins}m ${secs}s`;
    }
    return `${mins}m ${secs}s`;
  };

  const getAnswerLabel = (answerNum: string | null): string => {
    if (!answerNum) return '-';
    const num = parseInt(answerNum);
    return String.fromCharCode(64 + num); // 1->A, 2->B, etc.
  };

  const getStatusBadge = (question: Question) => {
    if (question.is_correct) {
      return <span className="badge bg-success">Correct</span>;
    } else if (question.status === 'skipped' || !question.user_answer) {
      return <span className="badge bg-secondary">Skipped</span>;
    } else {
      return <span className="badge bg-danger">Incorrect</span>;
    }
  };

  const getSubjectPerformance = () => {
    const subjectStats: { [key: string]: { correct: number; total: number } } = {};

    questions.forEach((q) => {
      const subject = q.chapter_name || 'Unknown';
      if (!subjectStats[subject]) {
        subjectStats[subject] = { correct: 0, total: 0 };
      }
      subjectStats[subject].total++;
      if (q.is_correct) {
        subjectStats[subject].correct++;
      }
    });

    return Object.entries(subjectStats).map(([subject, stats]) => ({
      subject,
      ...stats,
      percentage: ((stats.correct / stats.total) * 100).toFixed(1),
    }));
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div>Loading results...</div>
        </div>
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Error Loading Results</h4>
          <p>{error || 'Results not found'}</p>
          <button className="btn btn-primary" onClick={() => navigate('/mock-test')}>
            Back to Quiz List
          </button>
        </div>
      </div>
    );
  }

  const subjectPerformance = getSubjectPerformance();

  return (
    <div className="container my-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="fas fa-chart-bar me-2 text-primary"></i>
          Quiz Results
        </h2>
        <button className="btn btn-outline-secondary" onClick={() => navigate('/mock-test')}>
          <i className="fas fa-arrow-left me-2"></i>
          Back to Quiz List
        </button>
      </div>

      {/* Score Card */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row text-center">
            <div className="col-md-3 col-6 mb-3">
              <div className="h1 text-primary fw-bold">{result.total_score}</div>
              <div className="text-muted">Total Score</div>
              <div className="small text-muted">out of {result.total_marks}</div>
            </div>
            <div className="col-md-3 col-6 mb-3">
              <div className="h1 text-success fw-bold">
                {(typeof result.accuracy_percentage === 'number' 
                  ? result.accuracy_percentage 
                  : parseFloat(result.accuracy_percentage)).toFixed(1)}%
              </div>
              <div className="text-muted">Accuracy</div>
            </div>
            <div className="col-md-3 col-6 mb-3">
              <div className="h1 text-info fw-bold">
                {result.rank !== null ? `#${result.rank}` : '-'}
              </div>
              <div className="text-muted">Rank</div>
            </div>
            <div className="col-md-3 col-6 mb-3">
              <div className="h1 text-secondary fw-bold">{formatTime(result.time_spent)}</div>
              <div className="text-muted">Time Taken</div>
            </div>
          </div>

          <hr />

          <div className="row text-center">
            <div className="col-4">
              <div className="text-success fw-bold fs-4">{result.correct_answers}</div>
              <div className="small text-muted">Correct</div>
            </div>
            <div className="col-4">
              <div className="text-danger fw-bold fs-4">{result.incorrect_answers}</div>
              <div className="small text-muted">Incorrect</div>
            </div>
            <div className="col-4">
              <div className="text-secondary fw-bold fs-4">{result.skipped_questions}</div>
              <div className="small text-muted">Skipped</div>
            </div>
          </div>
        </div>
      </div>

      {/* Subject Performance */}
      {subjectPerformance.length > 0 && (
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-light">
            <h5 className="mb-0">Subject-wise Performance</h5>
          </div>
          <div className="card-body">
            {subjectPerformance.map((subject, idx) => (
              <div key={idx} className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="fw-semibold">{subject.subject}</span>
                  <span>
                    {subject.correct}/{subject.total} ({subject.percentage}%)
                  </span>
                </div>
                <div className="progress" style={{ height: '10px' }}>
                  <div
                    className={`progress-bar ${
                      parseFloat(subject.percentage) >= 75
                        ? 'bg-success'
                        : parseFloat(subject.percentage) >= 50
                        ? 'bg-warning'
                        : 'bg-danger'
                    }`}
                    style={{ width: `${subject.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Question-wise Analysis */}
      <div className="card shadow-sm">
        <div className="card-header bg-light">
          <h5 className="mb-0">Question-wise Analysis</h5>
        </div>
        <div className="card-body">
          {questions.map((question, idx) => (
            <div key={question.id} className="border rounded p-3 mb-3">
              <div
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  setExpandedQuestion(expandedQuestion === idx ? null : idx)
                }
              >
                <div className="d-flex align-items-center gap-3">
                  <span className="fw-bold">Q{idx + 1}</span>
                  {getStatusBadge(question)}
                  <span className="text-muted small">
                    Marks: {(typeof question.marks_obtained === 'number' 
                      ? question.marks_obtained 
                      : parseFloat(question.marks_obtained)).toFixed(1)} / {question.marks}
                  </span>
                  <span className="text-muted small">Time: {question.time_spent}s</span>
                </div>
                <i
                  className={`fas fa-chevron-${
                    expandedQuestion === idx ? 'up' : 'down'
                  }`}
                ></i>
              </div>

              {expandedQuestion === idx && (
                <div className="mt-3">
                  {/* Question */}
                  {question.question_img_url && (
                    <img
                      src={question.question_img_url}
                      alt="Question"
                      className="img-fluid mb-2"
                      style={{ maxHeight: '300px' }}
                    />
                  )}
                  {question.question_text && (
                    <p className="mb-2">{question.question_text}</p>
                  )}

                  {/* Answer Display - Conditional based on question_type */}
                  <div className="mt-2">
                    {question.question_type === 'mcq' ? (
                      // MCQ - Show 4 options
                      ['A', 'B', 'C', 'D'].map((opt, optIdx) => {
                        const optKey = `option_${opt.toLowerCase()}` as keyof Question;
                        const optText = question[optKey] as string;
                        const optValue = (optIdx + 1).toString();
                        const isUserAnswer = question.user_answer === optValue;
                        const isCorrect = question.correct_answer === optValue;

                        let className = 'border p-2 rounded mb-1';
                        if (isCorrect) {
                          className += ' border-success bg-success bg-opacity-10';
                        } else if (isUserAnswer) {
                          className += ' border-danger bg-danger bg-opacity-10';
                        }

                        return (
                          <div key={opt} className={className}>
                            <span className="fw-bold me-2">{opt}.</span>
                            {optText}
                            {isCorrect && (
                              <span className="badge bg-success ms-2">Correct</span>
                            )}
                            {isUserAnswer && !isCorrect && (
                              <span className="badge bg-danger ms-2">Your Answer</span>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      // Integer/Numerical - Show answer comparison
                      <div className="row">
                        <div className="col-md-6">
                          <div className={`border p-3 rounded ${
                            question.is_correct 
                              ? 'border-success bg-success bg-opacity-10' 
                              : 'border-danger bg-danger bg-opacity-10'
                          }`}>
                            <div className="fw-bold mb-2">Your Answer:</div>
                            <div className="h4 mb-0">
                              {question.user_answer || 'Not Answered'}
                              {question.is_correct ? (
                                <span className="badge bg-success ms-2">Correct</span>
                              ) : (
                                <span className="badge bg-danger ms-2">Incorrect</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="border border-success bg-success bg-opacity-10 p-3 rounded">
                            <div className="fw-bold mb-2">Correct Answer:</div>
                            <div className="h4 mb-0">
                              {question.correct_answer}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Solution */}
                  {question.solution && (
                    <div className="mt-3 p-3 bg-light rounded">
                      <h6 className="text-primary">
                        <i className="fas fa-lightbulb me-2"></i>
                        Solution:
                      </h6>
                      <p className="mb-0">{question.solution}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-4 text-center">
        <button
          className="btn btn-primary btn-lg me-2"
          onClick={() => navigate('/mock-test')}
        >
          <i className="fas fa-list me-2"></i>
          Back to Quiz List
        </button>
        <button
          className="btn btn-outline-primary btn-lg"
          onClick={() => window.print()}
        >
          <i className="fas fa-print me-2"></i>
          Print Results
        </button>
      </div>
    </div>
  );
};

export default WiziQuizResults;
